import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import { usePageContext, useRidesBulkActions } from "@ryde/redux";
import { bulkRecalculatePriceOptionsDrawerAF } from "@ryde/shared";

import {
  BulkRecalculateAdvancedContentStyled,
  BulkRecalculateAdvancedOptionsCheckbox,
  BulkRecalculateOptionsContentStyled,
  BulkRecalculateRateType,
  BulkRecalculateSelectionRadioGroup,
  BulkRecalculateSwitchOption,
  useBulkRecalculateSelection,
} from "./parts";

export const BulkRecalculatePriceOptionsRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const setBulkRecalculatePriceOptionsDrawerOpened = useSetRecoilState(
    bulkRecalculatePriceOptionsDrawerAF(entity),
  );

  const [shouldIncludeManualPrices, setShouldIncludeManualPrices] =
    useState(false);
  const [shouldSkipIfNoMatchingRate, setShouldSkipIfNoMatchingRate] =
    useState(false);
  const [recalculateDoneRides, setRecalculateDoneRides] = useState(false);
  const [allowSupplierChange, setAllowSupplierChange] = useState(false);
  const [allowVehicleTypeChange, setAllowVehicleTypeChange] = useState(false);
  const [allowPriceRateChange, setAllowPriceRateChange] = useState(false);
  const [isAdvancedSelectionEnabled, setIsAdvancedSelectionEnabled] =
    useState(false);
  const {
    selectionScope,
    onSelectionScopeChange,
    selectionCounts,
    switchCounts,
    selectedRideIdsForRecalculate,
  } = useBulkRecalculateSelection(
    BulkRecalculateRateType.PriceRate,
    isAdvancedSelectionEnabled,
  );
  const { bulkRecalculatePrice } = useRidesBulkActions();

  const onCloseClick = useEvent((): void => {
    setBulkRecalculatePriceOptionsDrawerOpened(false);
  });

  const recalculateCount = selectedRideIdsForRecalculate.length;

  const onNextClick = useEvent(async (): Promise<void> => {
    const result = await bulkRecalculatePrice({
      ids: selectedRideIdsForRecalculate,
      shouldIncludeManualPrices,
      shouldClearManualPriceAndSetDraftIfNoRate: !shouldSkipIfNoMatchingRate,
      recalculateDoneRides,
      allowSupplierChange,
      allowVehicleTypeChange,
      allowPriceRateChange,
    });

    if (result?.success) {
      setBulkRecalculatePriceOptionsDrawerOpened(false);
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseClick}>
      <FormDrawer
        onCloseDrawer={onCloseClick}
        onCancelClick={onCloseClick}
        title={t("general.RECALCULATE_PRICE")}
        onSaveClick={onNextClick}
        footerSaveLabel={t("general.RECALCULATE_PRICE_BUTTON", {
          count: recalculateCount,
        })}
        isSaveDisabled={recalculateCount === 0}
      >
        <BulkRecalculateOptionsContentStyled>
          <BulkRecalculateSwitchOption
            checked={shouldIncludeManualPrices}
            onChange={setShouldIncludeManualPrices}
            label={t("general.RECALCULATE_PRICE_INCLUDE_MANUAL_PRICES", {
              count: switchCounts.withManualPrice,
            })}
            helperText={t(
              "general.RECALCULATE_PRICE_INCLUDE_MANUAL_PRICES_HELPER",
            )}
          />
          <BulkRecalculateSwitchOption
            checked={recalculateDoneRides}
            onChange={setRecalculateDoneRides}
            label={t("general.RECALCULATE_PRICE_INCLUDE_DONE_RIDES", {
              count: switchCounts.withDoneStatus,
            })}
            helperText={t(
              "general.RECALCULATE_PRICE_INCLUDE_DONE_RIDES_HELPER",
            )}
          />
          <BulkRecalculateSwitchOption
            checked={allowSupplierChange}
            onChange={setAllowSupplierChange}
            label={t("general.RECALCULATE_PRICE_ALLOW_SUPPLIER_CHANGE")}
            helperText={t(
              "general.RECALCULATE_PRICE_ALLOW_SUPPLIER_CHANGE_HELPER",
            )}
          />
          <BulkRecalculateSwitchOption
            checked={allowVehicleTypeChange}
            onChange={setAllowVehicleTypeChange}
            label={t("general.RECALCULATE_PRICE_ALLOW_VEHICLE_TYPE_CHANGE")}
            helperText={t(
              "general.RECALCULATE_PRICE_ALLOW_VEHICLE_TYPE_CHANGE_HELPER",
            )}
          />
          <BulkRecalculateSwitchOption
            checked={allowPriceRateChange}
            onChange={setAllowPriceRateChange}
            label={t("general.RECALCULATE_PRICE_ALLOW_PRICE_RATE_CHANGE")}
            helperText={t(
              "general.RECALCULATE_PRICE_ALLOW_PRICE_RATE_CHANGE_HELPER",
            )}
          />
          <BulkRecalculateAdvancedOptionsCheckbox
            checked={isAdvancedSelectionEnabled}
            onChange={setIsAdvancedSelectionEnabled}
            label={t("general.RECALCULATE_ADVANCED_OPTIONS")}
          />
          {isAdvancedSelectionEnabled && (
            <BulkRecalculateAdvancedContentStyled>
              <BulkRecalculateSwitchOption
                checked={shouldSkipIfNoMatchingRate}
                onChange={setShouldSkipIfNoMatchingRate}
                label={t("general.RECALCULATE_PRICE_SKIP_IF_NO_RATE")}
                helperText={t(
                  "general.RECALCULATE_PRICE_SKIP_IF_NO_RATE_HELPER",
                )}
              />
              <BulkRecalculateSelectionRadioGroup
                rateType={BulkRecalculateRateType.PriceRate}
                selectionScope={selectionScope}
                onSelectionScopeChange={onSelectionScopeChange}
                selectionCounts={selectionCounts}
              />
            </BulkRecalculateAdvancedContentStyled>
          )}
        </BulkRecalculateOptionsContentStyled>
      </FormDrawer>
    </BaseDrawer>
  );
};
